import React from 'react';
import Tooltip from '@/components/atoms/Tooltip';
import { type ColumnDef } from '@/components/organism/Table';
import { Cell, PillCell } from '@/components/atoms';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { DEVICE_PROPS } from '@/constant/device/device.constant';
import { tid } from '@/utils/testId';
import { DEVICE_TEST_IDS as TID } from '@/constant/testIds/device.testIds';
import type { IDeviceProps } from './device.interface';

/* ── Column definitions ─────────────────────────────────────────────────── */
export function buildColumns(
  isCopy: string,
  onCopyClick: (key: string) => void,
): ColumnDef<IDeviceProps>[] {
  return [
    {
      accessor: 'sourceOperatorId',
      header: DEVICE_PROPS.COLUMNS.OPERATOR_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_OPERATOR_ID(`${idx}`))}
          value={item.sourceOperatorId}
          tooltip
          variant="primary"
          copy={{ copyKey: `opid-${item.sourceId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'sourceMarketId',
      header: DEVICE_PROPS.COLUMNS.MARKET_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_MARKET_ID(`${idx}`))}
          value={item.sourceMarketId}
          tooltip
          variant="primary"
          copy={{ copyKey: `mkid-${item.sourceId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'sourceId',
      header: DEVICE_PROPS.COLUMNS.DEVICE_ID,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_DEVICE_ID(`${idx}`))}
          value={item.sourceId}
          tooltip
          variant="mono"
          copy={{ copyKey: `devid-${item.sourceId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'sn',
      header: DEVICE_PROPS.COLUMNS.SERIAL_NO,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_SERIAL_NO(`${idx}`))}
          value={item.sn}
          tooltip
          variant="mono"
          copy={{ copyKey: `sn-${item.sourceId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'type',
      header: DEVICE_PROPS.COLUMNS.TYPE,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_TYPE(`${idx}`))}
          value={item.type ? item.type.toUpperCase() : undefined}
          className="font-medium uppercase"
        />
      ),
    },
    {
      accessor: 'lastSyncDtm',
      header: DEVICE_PROPS.COLUMNS.LAST_SYNC,
      render: (item, idx) =>
        <Cell
          testId={tid(TID.COL_LAST_SYNC(`${idx}`))}
          value={item.lastSyncDtm}
          format="datetime"
          variant="muted"
          size="text-[11px]"
        />
    },
    {
      accessor: 'deletedOn',
      header: DEVICE_PROPS.COLUMNS.STATUS,
      render: (item, idx) => (
        <Tooltip
          content={
            item.deletedOn
              ? `Deleted: ${getDateTime(item.deletedOn)}`
              : 'Active'
          }
          placement="right"
        >
          <div>
          <PillCell
            testId={tid(TID.COL_STATUS(`${idx}`))}
            value={item.deletedOn ? 'Inactive' : 'Active'}
            preset={item.deletedOn ? 'inactive' : 'active'}
            size="text-[11px]"
            className="border font-semibold cursor-default"
            />
            </div>
        </Tooltip>
      ),
    },
    {
      accessor: 'updatedOn',
      header: DEVICE_PROPS.COLUMNS.UPDATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item.updatedOn}
          variant="muted"
          size="text-[11px]"
          tooltip={getDateTime(item.updatedOn)}
          tooltipPlacement="top"
        >
          {item.updatedOn ? getDateTimeAgo(item.updatedOn) : '—'}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: DEVICE_PROPS.COLUMNS.CREATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(`${idx}`))}
          value={item.createdOn}
          format="datetime"
          variant="muted"
          size="text-[11px]"
        />
      ),
    },
  ];
}
